// src/pages/FinancialAnalysis.js
import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Container,
} from "@mui/material";
import {
  ArrowBack,
  ShoppingCart,
  Sell,
  Savings,
  Assessment,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCollection } from "../hooks/useCollection";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";

// Colores EPC
const EPC = {
  blueDeep: "#005f65",
  blue: "#00afa3",
  blueLight: "#00e1d2",
  aqua: "#42fff2",
  mint: "#95ffef",
  greenDeep: "#0e502d",
  green: "#00935d",
  greenLight: "#00c07c",
  greenBright: "#00ffbb",
  greenSoft: "#56ffd2",
};

// Función auxiliar para formatear moneda CORREGIDA
const formatCurrency = (value) => {
  // Asegurarnos de que value sea un número
  const num = Number(value) || 0;
  
  // Formatear con exactamente 2 decimales
  const parts = num.toFixed(2).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Agregar separadores de miles a la parte entera
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  // Unir con coma para decimales
  return `${formattedInteger},${decimalPart}`;
};

// Componente de gráfico de barras SVG responsive
const BarChart = ({ data, maxValue, salesColor, purchasesColor, height = 250 }) => {
  const chartHeight = height - 60;
  const barWidth = 35;
  const gap = 8;
  const totalWidth = Math.max(data.length * 120, 300);

  return (
    <Box sx={{ width: "100%", overflowX: "auto", display: "flex", justifyContent: "center" }}>
      <svg 
        width="100%" 
        height={height} 
        viewBox={`0 0 ${totalWidth} ${height}`} 
        preserveAspectRatio="xMidYMid meet"
      >
        {data.map((item, index) => {
          const salesHeight = maxValue > 0 ? (item.totalSales / maxValue) * chartHeight : 0;
          const purchasesHeight = maxValue > 0 ? (item.totalPurchases / maxValue) * chartHeight : 0;
          const x = index * 120 + (120 - barWidth * 2 - gap) / 2;

          return (
            <g key={index}>
              {/* Ventas */}
              <rect
                x={x}
                y={chartHeight - salesHeight}
                width={barWidth}
                height={salesHeight}
                fill={salesColor}
                rx="4"
                ry="4"
              >
                <title>Ventas Q{index + 1}: {formatCurrency(item.totalSales)}€</title>
              </rect>
              
              {/* Compras */}
              <rect
                x={x + barWidth + gap}
                y={chartHeight - purchasesHeight}
                width={barWidth}
                height={purchasesHeight}
                fill={purchasesColor}
                rx="4"
                ry="4"
              >
                <title>Compras Q{index + 1}: {formatCurrency(item.totalPurchases)}€</title>
              </rect>

              {/* Labels */}
              <text
                x={x + barWidth + gap / 2}
                y={chartHeight + 20}
                textAnchor="middle"
                fontSize="12"
                fontWeight="bold"
                fill="#666"
              >
                Q{index + 1}
              </text>
              
              {/* Margen % */}
              <text
                x={x + barWidth + gap / 2}
                y={chartHeight + 40}
                textAnchor="middle"
                fontSize="11"
                fill={item.margin >= 0 ? EPC.green : "#f44336"}
              >
                {item.totalSales > 0 || item.totalPurchases > 0 ? `${item.marginPercentage.toFixed(2)}%` : ""}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

// Componente de gráfico circular SVG responsive
const DonutChart = ({ data, labels, colors, size = 200 }) => {
  const total = data.reduce((sum, val) => sum + val, 0);
  const center = size / 2;
  const radius = size * 0.3;
  const strokeWidth = size * 0.12;
  
  let currentAngle = -90;
  
  const slices = data.map((value, index) => {
    const percentage = total > 0 ? value / total : 0;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    if (percentage === 0) return null;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const pathData = [
      `M ${center} ${center}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      "Z",
    ].join(" ");

    return (
      <g key={index}>
        <path d={pathData} fill={colors[index]} stroke="white" strokeWidth="2" />
      </g>
    );
  });

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices}
        <circle cx={center} cy={center} r={radius - strokeWidth} fill="white" />
        <text
          x={center}
          y={center - 10}
          textAnchor="middle"
          fontSize={size * 0.07}
          fontWeight="bold"
          fill={EPC.blueDeep}
        >
          {formatCurrency(total)}€
        </text>
        <text
          x={center}
          y={center + 15}
          textAnchor="middle"
          fontSize={size * 0.05}
          fill="#666"
        >
          Total
        </text>
      </svg>
    </Box>
  );
};

const FinancialAnalysis = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: salesInvoices, loading: loadingSales } = useCollection("salesInvoices");
  const { data: purchaseInvoices, loading: loadingPurchases } = useCollection("purchaseInvoices");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // FILTRAR SOLO FACTURAS PAGADAS
  const paidSalesInvoices = useMemo(() => {
    return salesInvoices?.filter((inv) => inv.isActive !== false && inv.status === "paid") || [];
  }, [salesInvoices]);

  const paidPurchaseInvoices = useMemo(() => {
    return purchaseInvoices?.filter((inv) => inv.isActive !== false && inv.status === "paid") || [];
  }, [purchaseInvoices]);

  // Obtener años disponibles (solo de facturas pagadas)
  const availableYears = useMemo(() => {
    const years = new Set();
    const allInvoices = [...paidSalesInvoices, ...paidPurchaseInvoices];
    allInvoices.forEach((inv) => {
      if (inv.issueDate) {
        years.add(new Date(inv.issueDate).getFullYear());
      }
    });
    if (years.size === 0) {
      years.add(new Date().getFullYear());
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [paidSalesInvoices, paidPurchaseInvoices]);

  // Función para obtener el trimestre
  const getQuarter = (date) => {
    if (!date) return 0;
    const month = new Date(date).getMonth();
    return Math.floor(month / 3) + 1;
  };

  // Calcular datos trimestrales (SOLO FACTURAS PAGADAS)
  const quarterlyData = useMemo(() => {
    const quarters = [1, 2, 3, 4];
    const data = quarters.map((quarter) => {
      const quarterSales = paidSalesInvoices.filter((inv) => {
        if (!inv.issueDate) return false;
        const invDate = new Date(inv.issueDate);
        return (
          invDate.getFullYear() === selectedYear &&
          getQuarter(inv.issueDate) === quarter
        );
      });

      // Usar baseAmount (sin IVA) para cálculos de beneficio
      const totalSales = quarterSales.reduce((sum, inv) => sum + (Number(inv.baseAmount) || 0), 0);
      const totalSalesWithTax = quarterSales.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);

      const quarterPurchases = paidPurchaseInvoices.filter((inv) => {
        if (!inv.issueDate) return false;
        const invDate = new Date(inv.issueDate);
        return (
          invDate.getFullYear() === selectedYear &&
          getQuarter(inv.issueDate) === quarter
        );
      });

      // Usar baseAmount (sin IVA) para cálculos de beneficio
      const totalPurchases = quarterPurchases.reduce((sum, inv) => sum + (Number(inv.baseAmount) || 0), 0);
      const totalPurchasesWithTax = quarterPurchases.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);

      const margin = totalSales - totalPurchases;
      const marginPercentage = totalSales > 0 ? ((margin / totalSales) * 100) : 0;

      return {
        quarter,
        label: `Q${quarter}`,
        months: quarter === 1 ? "Ene-Mar" : quarter === 2 ? "Abr-Jun" : quarter === 3 ? "Jul-Sep" : "Oct-Dic",
        totalSales, // Base imponible (sin IVA)
        totalSalesWithTax, // Total con IVA (solo para mostrar)
        totalPurchases, // Base imponible (sin IVA)
        totalPurchasesWithTax, // Total con IVA (solo para mostrar)
        margin,
        marginPercentage,
        salesCount: quarterSales.length,
        purchasesCount: quarterPurchases.length,
      };
    });

    return data;
  }, [paidSalesInvoices, paidPurchaseInvoices, selectedYear]);

  // Totales anuales (SOLO FACTURAS PAGADAS)
  const yearlyTotals = useMemo(() => {
    const totalSales = quarterlyData.reduce((sum, q) => sum + q.totalSales, 0);
    const totalPurchases = quarterlyData.reduce((sum, q) => sum + q.totalPurchases, 0);
    const totalMargin = totalSales - totalPurchases;
    const totalMarginPercentage = totalSales > 0 ? ((totalMargin / totalSales) * 100) : 0;

    const totalSalesWithTax = quarterlyData.reduce((sum, q) => sum + q.totalSalesWithTax, 0);
    const totalPurchasesWithTax = quarterlyData.reduce((sum, q) => sum + q.totalPurchasesWithTax, 0);

    return {
      totalSales, // Base imponible (para cálculos de margen)
      totalSalesWithTax, // Con IVA (para mostrar en KPIs)
      totalPurchases, // Base imponible (para cálculos de margen)
      totalPurchasesWithTax, // Con IVA (para mostrar en KPIs)
      totalMargin,
      totalMarginPercentage,
      totalInvoices: quarterlyData.reduce((sum, q) => sum + q.salesCount + q.purchasesCount, 0),
    };
  }, [quarterlyData]);

  const maxBarValue = Math.max(
    ...quarterlyData.map((q) => Math.max(q.totalSalesWithTax, q.totalPurchasesWithTax)),
    1
  );

  if (loadingSales || loadingPurchases) return <LoadingSpinner />;

  return (
    <Box sx={{ 
      width: "100%",
      minHeight: "100vh",
      bgcolor: "background.default",
      px: { xs: 1, sm: 2, md: 3 },
      py: { xs: 2, sm: 3 }
    }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          width: "100%",
          borderRadius: { xs: "12px", sm: "16px", md: "20px" },
          border: "1px solid",
          borderColor: theme.palette.divider,
          background: `linear-gradient(135deg, ${EPC.blueDeep}15, ${EPC.blue}10)`,
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          width="100%"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={() => navigate("/")}
              sx={{ color: EPC.blueDeep, bgcolor: `${EPC.blueDeep}15`, flexShrink: 0 }}
            >
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2rem" },
                  background: `linear-gradient(135deg, ${EPC.blueDeep}, ${EPC.blue})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  wordBreak: "break-word"
                }}
              >
                Análisis Financiero Trimestral
              </Typography>
            </Box>
          </Stack>

          {availableYears.length > 0 && (
            <ToggleButtonGroup
              value={selectedYear}
              exclusive
              onChange={(e, newYear) => newYear && setSelectedYear(newYear)}
              size="small"
              sx={{
                flexShrink: 0,
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                maxHeight: "40px",
                "& .MuiToggleButton-root": {
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: 0.5,
                  borderRadius: "12px !important",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  mx: 0.5,
                  mb: { xs: 0.5, sm: 0 },
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  lineHeight: 1.2,
                  minHeight: "unset",
                  height: "auto",
                  "&.Mui-selected": {
                    bgcolor: EPC.blueDeep,
                    color: "white",
                    "&:hover": {
                      bgcolor: EPC.blue,
                    },
                  },
                },
              }}
            >
              {availableYears.map((year) => (
                <ToggleButton key={year} value={year}>
                  {year}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Stack>
      </Paper>

      {/* KPIs Cards - Mostrando totales con IVA de facturas PAGADAS */}
      <Box sx={{ 
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gap: { xs: 1, sm: 2 },
        mb: { xs: 2, sm: 3 },
        width: "100%"
      }}>
        <Card
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: `linear-gradient(135deg, ${EPC.blue}15, ${EPC.blue}05)`,
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Sell sx={{ color: EPC.blue, fontSize: { xs: 24, sm: 28 } }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Ventas Cobradas
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={700} color={EPC.blueDeep} sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
              {formatCurrency(yearlyTotals.totalSalesWithTax)}€
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Base: {formatCurrency(yearlyTotals.totalSales)}€ + IVA
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: `linear-gradient(135deg, ${EPC.green}15, ${EPC.green}05)`,
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <ShoppingCart sx={{ color: EPC.green, fontSize: { xs: 24, sm: 28 } }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Compras Pagadas
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={700} color={EPC.greenDeep} sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}>
              {formatCurrency(yearlyTotals.totalPurchasesWithTax)}€
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Base: {formatCurrency(yearlyTotals.totalPurchases)}€ + IVA
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: yearlyTotals.totalMargin >= 0 ? EPC.blue : "#f44336",
            background: yearlyTotals.totalMargin >= 0
              ? `linear-gradient(135deg, ${EPC.blueLight}15, ${EPC.blue}05)`
              : "linear-gradient(135deg, #f4433615, #f4433605)",
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Savings
                sx={{
                  color: yearlyTotals.totalMargin >= 0 ? EPC.blueLight : "#f44336",
                  fontSize: { xs: 24, sm: 28 },
                }}
              />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Margen Bruto Real
              </Typography>
            </Stack>
            <Typography
              variant="h5"
              fontWeight={700}
              color={yearlyTotals.totalMargin >= 0 ? EPC.blueLight : "#f44336"}
              sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
            >
              {formatCurrency(yearlyTotals.totalMargin)}€
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sin IVA
            </Typography>
          </CardContent>
        </Card>

        <Card
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: `linear-gradient(135deg, ${EPC.aqua}15, ${EPC.mint}05)`,
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Assessment sx={{ color: EPC.aqua, fontSize: { xs: 24, sm: 28 } }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                Rentabilidad Real
              </Typography>
            </Stack>
            <Typography
              variant="h5"
              fontWeight={700}
              color={yearlyTotals.totalMarginPercentage >= 0 ? EPC.greenLight : "#f44336"}
              sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
            >
              {yearlyTotals.totalMarginPercentage.toFixed(2)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Margen / Ventas
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Charts - Mostrando base imponible en gráficos */}
      <Box sx={{ 
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
        gap: { xs: 1, sm: 3 },
        mb: { xs: 2, sm: 3 },
        width: "100%"
      }}>
        <Paper
          elevation={2}
          sx={{
            width: "100%",
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" fontWeight={600} mb={2} sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            Ventas vs Compras por Trimestre
          </Typography>
          {yearlyTotals.totalInvoices > 0 ? (
            <>
              <BarChart
                data={quarterlyData.map(q => ({
                  ...q,
                  totalSales: q.totalSales, // Base imponible
                  totalPurchases: q.totalPurchases, // Base imponible
                }))}
                maxValue={maxBarValue}
                salesColor={EPC.blueLight}
                purchasesColor={EPC.greenLight}
                height={280}
              />
              <Stack 
                direction="row" 
                spacing={3} 
                justifyContent="center" 
                mt={2} 
                flexWrap="wrap"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: EPC.blueLight }} />
                  <Typography variant="caption">Ventas cobradas (Base imponible)</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: EPC.greenLight }} />
                  <Typography variant="caption">Compras pagadas (Base imponible)</Typography>
                </Stack>
              </Stack>
            </>
          ) : (
            <EmptyState 
              title="Sin datos" 
              description="No hay facturas pagadas en este año" 
            />
          )}
        </Paper>

        <Paper
          elevation={2}
          sx={{
            width: "100%",
            p: { xs: 2, sm: 3 },
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
          }}
        >
          <Typography 
            variant="h6" 
            fontWeight={600} 
            mb={2} 
            alignSelf="flex-start"
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Distribución Margen Bruto
          </Typography>
          {yearlyTotals.totalSales > 0 || yearlyTotals.totalPurchases > 0 ? (
            <>
              <DonutChart
                data={[yearlyTotals.totalMargin > 0 ? yearlyTotals.totalMargin : 0, yearlyTotals.totalPurchases]}
                colors={[EPC.blue, EPC.green]}
                size={220}
              />
              <Stack 
                direction="row" 
                spacing={3} 
                mt={2} 
                flexWrap="wrap" 
                justifyContent="center"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: EPC.blue }} />
                  <Typography variant="caption">Margen Bruto Real</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: EPC.green }} />
                  <Typography variant="caption">Costes pagados (Base imponible)</Typography>
                </Stack>
              </Stack>
            </>
          ) : (
            <EmptyState 
              title="Sin datos" 
              description="No hay facturas pagadas para mostrar" 
            />
          )}
        </Paper>
      </Box>

      {/* Tabla Detallada - Mostrando base imponible */}
      {yearlyTotals.totalInvoices > 0 && (
        <Paper
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <TableContainer sx={{ maxHeight: { xs: 400, sm: "none" } }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.1)" : "rgba(0, 95, 101, 0.05)" }}>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Trimestre</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Periodo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Ventas Cobradas (Base)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Compras Pagadas (Base)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Margen Real</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Rentabilidad Real</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Facturas Pagadas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quarterlyData.map((q) => (
                  <TableRow
                    key={q.quarter}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0, 95, 101, 0.02)",
                      },
                    }}
                  >
                    <TableCell align="center">
                      <Chip
                        label={q.label}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: q.margin >= 0 ? `${EPC.blue}20` : "#f4433620",
                          color: q.margin >= 0 ? EPC.blue : "#f44336",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <CalendarToday sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography variant="body2">{q.months}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color={EPC.blueDeep}>
                        {formatCurrency(q.totalSales)}€
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        +IVA: {formatCurrency(q.totalSalesWithTax)}€
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color={EPC.greenDeep}>
                        {formatCurrency(q.totalPurchases)}€
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        +IVA: {formatCurrency(q.totalPurchasesWithTax)}€
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                        {q.margin > 0 ? (
                          <ArrowUpward sx={{ fontSize: 16, color: "success.main" }} />
                        ) : q.margin < 0 ? (
                          <ArrowDownward sx={{ fontSize: 16, color: "error.main" }} />
                        ) : (
                          <Box sx={{ width: 16, height: 16 }} />
                        )}
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={q.margin >= 0 ? "success.main" : "error.main"}
                        >
                          {formatCurrency(q.margin)}€
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${q.marginPercentage.toFixed(2)}%`}
                        size="small"
                        color={q.marginPercentage >= 0 ? "success" : "error"}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {q.salesCount + q.purchasesCount}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Totales */}
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.15)"
                        : "rgba(0, 95, 101, 0.1)",
                    "& td": { fontWeight: 700 },
                  }}
                >
                  <TableCell align="center" colSpan={2}>
                    <Typography fontWeight={700}>TOTAL ANUAL</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={700} color={EPC.blueDeep}>
                      {formatCurrency(yearlyTotals.totalSales)}€
                    </Typography>
                    <Typography variant="caption">
                      +IVA: {formatCurrency(yearlyTotals.totalSalesWithTax)}€
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={700} color={EPC.greenDeep}>
                      {formatCurrency(yearlyTotals.totalPurchases)}€
                    </Typography>
                    <Typography variant="caption">
                      +IVA: {formatCurrency(yearlyTotals.totalPurchasesWithTax)}€
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      fontWeight={700}
                      color={yearlyTotals.totalMargin >= 0 ? "success.main" : "error.main"}
                    >
                      {formatCurrency(yearlyTotals.totalMargin)}€
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${yearlyTotals.totalMarginPercentage.toFixed(2)}%`}
                      color={yearlyTotals.totalMarginPercentage >= 0 ? "success" : "error"}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={700}>{yearlyTotals.totalInvoices}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Mensaje cuando no hay facturas pagadas */}
      {yearlyTotals.totalInvoices === 0 && (
        <Paper
          elevation={2}
          sx={{
            width: "100%",
            p: 4,
            borderRadius: { xs: "12px", sm: "16px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            textAlign: "center",
            boxSizing: "border-box",
          }}
        >
          <EmptyState 
            title="No hay facturas pagadas" 
            description={`No se encontraron facturas marcadas como pagadas en ${selectedYear}. Marca facturas como pagadas para ver el análisis financiero real.`}
          />
        </Paper>
      )}
    </Box>
  );
};

export default FinancialAnalysis;